import { all, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { closeDialog } from '../../dialog/dialog.saga-services';
import { dialogSelectors } from '../../dialog/dialog.selectors';
import * as paymentService from '../../resource/payment/payment.saga-service';
import * as invoicesPageService from '../invoices/invoices.saga-services';
import * as paymentMethodService from '../../resource/payment-method/payment-method.saga-service';
import * as subscriptionService from '../../resource/subscription/subscription.saga-service';
import { getSubscriptionAccess } from '../../resource/subscription-access/subscription-access.saga-services';
import { IPaymentMethod, ISetupIntentSession } from '../../resource/payment-method/payment-method.types';
import { ISubscription } from '../../resource/subscription/subscription.types';
import { IAddPaymentMethodDialogProps } from '../../../components/dialogs/AddPaymentMethodDialog/types';
import { addFlashMessage } from '../../../utils/flashMessages.utils';
import * as pageStateService from '../page-state/page-state.saga-services';
import * as pageInitializerService from '../page.initializer.saga-service';
import * as api from '../../api/request-config/subscription.api';
import { apiRequest } from '../../api/api.saga-services';
import * as actions from './subscription.actions';
import * as types from './subscription.types';

export function* initialSubscriptionPage() {
  yield call(pageStateService.initPage, 'subscription');
  yield call(pageInitializerService.initPage);

  yield all([
    call(subscriptionService.getSubscription),
    call(paymentMethodService.getPaymentMethods),
    call(paymentService.getPayments),
    call(invoicesPageService.fetchInvoices, { page: 1 }),
    call(fetchBillingInfo),
  ]);

  yield call(pageStateService.completedInit, 'subscription');
}

export function* clearSubscriptionPageStorage() {
  yield put(actions.clearSetupSession());
  yield put(actions.clearBillingInfo());
  yield call(invoicesPageService.clearInvoicesPageStorage);
}

export function* fetchBillingInfo() {
  const billingInfo = (yield call(apiRequest, api.getBillingInfoApi())) as types.IBillingInfo | null;
  if (!billingInfo) {
    return;
  }

  yield put(actions.storeBillingInfo(billingInfo));
}

export function* saveBillingInfo(payload: types.IBillingInfoInput) {
  const billingInfo = (yield call(apiRequest, api.saveBillingInfoApi(payload))) as types.IBillingInfo | null;
  if (!billingInfo) {
    return false;
  }

  yield put(actions.storeBillingInfo(billingInfo));
  yield call(addFlashMessage, { type: 'success', message: 'Billing info saved successfully' });
  return true;
}

export function* createSetupIntent() {
  yield put(actions.clearSetupSession());
  const session = (yield call(paymentMethodService.createSetupIntent)) as ISetupIntentSession | null;
  if (!session) {
    return;
  }

  yield put(actions.storeSetupSession(session));
}

export function* addPaymentMethod(payload: types.IAddPaymentMethodPayload) {
  const dialogProps = (yield select(
    dialogSelectors.selectDialogProps,
    'addPaymentMethodDialog',
  )) as IAddPaymentMethodDialogProps | null;
  const endTrialOnSave = Boolean(dialogProps?.endTrialOnSave);

  const paymentMethod = (yield call(
    paymentMethodService.createPaymentMethod,
    payload.token,
    payload.externalSetupRef,
  )) as IPaymentMethod | undefined;
  if (!paymentMethod) {
    return;
  }

  const [, subscription] = (yield all([
    call(paymentMethodService.getPaymentMethods),
    call(subscriptionService.getSubscription),
  ])) as [IPaymentMethod[] | undefined, ISubscription | null];
  yield call(getSubscriptionAccess);
  yield put(actions.clearSetupSession());
  yield call(closeDialog, 'addPaymentMethodDialog');

  if (endTrialOnSave) {
    yield call(startBilling);
    return;
  }

  if (subscription?.hasPaymentIssue) {
    yield call(startBilling);
    return;
  }

  yield call(addFlashMessage, { type: 'success', message: 'Payment method saved successfully' });
}

export function* setDefaultPaymentMethod(paymentMethodId: IPaymentMethod['id']) {
  const paymentMethod = (yield call(paymentMethodService.setDefaultPaymentMethod, paymentMethodId)) as
    | IPaymentMethod
    | undefined;
  if (!paymentMethod) {
    return;
  }

  const [, subscription] = (yield all([
    call(paymentMethodService.getPaymentMethods),
    call(subscriptionService.getSubscription),
    call(getSubscriptionAccess),
  ])) as [IPaymentMethod[] | undefined, ISubscription | null, unknown];

  if (subscription?.hasPaymentIssue) {
    yield call(startBilling);
    return;
  }

  yield call(addFlashMessage, { type: 'success', message: 'Default payment method updated successfully' });
}

export function* deletePaymentMethod(paymentMethodId: IPaymentMethod['id']) {
  const deleted = (yield call(paymentMethodService.deletePaymentMethod, paymentMethodId)) as boolean;
  if (!deleted) {
    return;
  }

  yield all([call(paymentMethodService.getPaymentMethods), call(subscriptionService.getSubscription)]);
  yield call(addFlashMessage, { type: 'success', message: 'Payment method removed successfully' });
}

export function* startBilling() {
  const subscription = (yield call(subscriptionService.startBilling)) as ISubscription | undefined;
  if (!subscription) {
    return;
  }

  if (subscription.hasPaymentIssue) {
    yield call(addFlashMessage, {
      type: 'warning',
      message: 'Payment could not be completed. Please update your payment method and try again.',
    });
    return;
  }

  yield call(addFlashMessage, {
    type: 'success',
    message: 'Subscription updated successfully',
  });

  yield put(push('/settings/subscription'));
}

export function* cancelSubscription() {
  const subscription = (yield call(subscriptionService.cancelSubscription)) as ISubscription | undefined;
  if (!subscription) {
    return;
  }

  yield call(addFlashMessage, { type: 'success', message: 'Subscription canceled successfully' });
}

export function* reactivateSubscription() {
  const subscription = (yield call(subscriptionService.reactivateSubscription)) as ISubscription | undefined;
  if (!subscription) {
    return;
  }

  yield call(addFlashMessage, { type: 'success', message: 'Subscription reactivated successfully' });
}
